// Color helpers shared by the SPA and the server-rendered KB pages (which set the header
// foreground and the readable link tint server-side so there's no flash). `contrastText` is
// the same WCAG pick as client/src/lib/color.js.

export const isHexColor = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);

// The light theme's page background (styles.css `--bg`): the darkest light surface link text
// sits on, so a color that reads there reads on every lighter one (cards, white) too.
export const PAGE_BG_LIGHT = '#f6f8fc';

// '#abc' / '#aabbcc' → [r, g, b]; anything else → null.
function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)].map((x) => parseInt(x, 16));
}

const toHex = (rgb) => `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;

// WCAG 2 relative luminance + contrast ratio (1 … 21).
const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
const luminance = ([r, g, b]) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
const ratio = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

// Contrast ratio between two hex colors; 1 when either can't be parsed.
export function contrastRatio(a, b) {
  const x = parseHex(a);
  const y = parseHex(b);
  return x && y ? ratio(luminance(x), luminance(y)) : 1;
}

// Readable text color (dark/light) for a given background hex: whichever of the app's
// near-black (#1e2a3b, L≈0.0226) and white gives the higher contrast ratio.
export function contrastText(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return '#1e2a3b';
  const L = luminance(rgb);
  return ratio(L, 0.0226) >= ratio(L, 1) ? '#1e2a3b' : '#ffffff';
}

// The nearest darker shade of `hex` that reads as TEXT on `bg` — WCAG contrast ≥ `min` (AA
// for body text by default): the color itself when it already does, else every channel is
// scaled down 5% a step until it does, so the hue survives (a yellow brand link comes out
// gold, not grey). Meant for light backgrounds: on one too dark to ever reach `min` by
// darkening, or for an unparseable input, the input comes back unchanged.
export function readableOn(hex, bg = '#ffffff', min = 4.5) {
  const rgb = parseHex(hex);
  const bgRgb = parseHex(bg);
  if (!rgb || !bgRgb) return hex;
  const bgL = luminance(bgRgb);
  if (ratio(0, bgL) < min) return hex; // even black wouldn't do
  let cur = rgb;
  while (ratio(luminance(cur), bgL) < min) cur = cur.map((c) => Math.floor(c * 0.95));
  return toHex(cur);
}
